import { DashboardStats, Transaction, ApiKey } from '@ppp/types';

// State & API Configuration
const DEFAULT_WORKER_URL = 'https://ppp-test-worker.theasmat.workers.dev';
let currentApiBase = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? ''
  : (localStorage.getItem('ppp_api_url') || DEFAULT_WORKER_URL);

let currentPasskey = localStorage.getItem('phonepe_passkey') || 'test-user-passkey-123';
let currentPage = 1;
let currentSearch = '';
let autoRefreshTimer: any = null;
let watcherTimer: any = null;

// Tab Switching Elements
const tabBtns = document.querySelectorAll('.tab-btn');
const tabContents = document.querySelectorAll('.tab-content');

tabBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    tabBtns.forEach((b) => b.classList.remove('active'));
    tabContents.forEach((c) => c.classList.remove('active'));

    btn.classList.add('active');
    const tabName = btn.getAttribute('data-tab');
    const target = document.getElementById(`tab-${tabName}`);
    if (target) target.classList.add('active');
  });
});

// DOM Elements: Header & Status
const sessionIndicator = document.getElementById('sessionIndicator') as HTMLDivElement;
const sessionStatusText = document.getElementById('sessionStatusText') as HTMLSpanElement;
const authBtn = document.getElementById('authBtn') as HTMLButtonElement;
const authModal = document.getElementById('authModal') as HTMLDivElement;
const passkeyInput = document.getElementById('passkeyInput') as HTMLInputElement;
const apiUrlInput = document.getElementById('apiUrlInput') as HTMLInputElement;
const savePasskeyBtn = document.getElementById('savePasskeyBtn') as HTMLButtonElement;
const toastEl = document.getElementById('toast') as HTMLDivElement;

// DOM Elements: Stats
const todayAmountEl = document.getElementById('todayAmount') as HTMLDivElement;
const todayCountEl = document.getElementById('todayCount') as HTMLSpanElement;
const totalAmountEl = document.getElementById('totalAmount') as HTMLDivElement;
const totalCountEl = document.getElementById('totalCount') as HTMLSpanElement;
const lastSyncAgoEl = document.getElementById('lastSyncAgo') as HTMLDivElement;

// DOM Elements: Feed Controls
const searchInput = document.getElementById('searchInput') as HTMLInputElement;
const autoRefreshToggle = document.getElementById('autoRefreshToggle') as HTMLInputElement;
const refreshBtn = document.getElementById('refreshBtn') as HTMLButtonElement;
const pollBtn = document.getElementById('pollBtn') as HTMLButtonElement;
const reconcileBtn = document.getElementById('reconcileBtn') as HTMLButtonElement;

// DOM Elements: Tables & Cards
const txTableBody = document.getElementById('txTableBody') as HTMLTableSectionElement;
const txCardsList = document.getElementById('txCardsList') as HTMLDivElement;
const paginationInfo = document.getElementById('paginationInfo') as HTMLSpanElement;
const pageIndicator = document.getElementById('pageIndicator') as HTMLSpanElement;
const prevPageBtn = document.getElementById('prevPageBtn') as HTMLButtonElement;
const nextPageBtn = document.getElementById('nextPageBtn') as HTMLButtonElement;

// DOM Elements: Test Payment Console (One At A Time)
const modePayBtn = document.getElementById('modePayBtn') as HTMLButtonElement;
const modeQrBtn = document.getElementById('modeQrBtn') as HTMLButtonElement;
const payView = document.getElementById('payView') as HTMLDivElement;
const qrView = document.getElementById('qrView') as HTMLDivElement;

const testAmountInput = document.getElementById('testAmountInput') as HTMLInputElement;
const presetChips = document.querySelectorAll<HTMLButtonElement>('.preset-chip');
const summaryOrderRef = document.getElementById('summaryOrderRef') as HTMLSpanElement;
const testVpaInput = document.getElementById('testVpaInput') as HTMLInputElement;
const testRefInput = document.getElementById('testRefInput') as HTMLInputElement;
const testNoteInput = document.getElementById('testNoteInput') as HTMLInputElement;

const payDirectBtn = document.getElementById('payDirectBtn') as HTMLButtonElement;
const payBtnLabel = document.getElementById('payBtnLabel') as HTMLSpanElement;
const copyUpiLinkBtn = document.getElementById('copyUpiLinkBtn') as HTMLButtonElement;
const testQrImage = document.getElementById('testQrImage') as HTMLImageElement;
const radarText = document.getElementById('radarText') as HTMLSpanElement;

// DOM Elements: UTR Lookup
const lookupForm = document.getElementById('lookupForm') as HTMLFormElement;
const lookupInput = document.getElementById('lookupInput') as HTMLInputElement;
const lookupSubmitBtn = document.getElementById('lookupSubmitBtn') as HTMLButtonElement;
const lookupResultBox = document.getElementById('lookupResultBox') as HTMLDivElement;

// Initialize default test reference
function initOrderRef() {
  const randNum = Math.floor(100000 + Math.random() * 900000);
  const ref = `TEST-${randNum}`;
  if (testRefInput) testRefInput.value = ref;
  if (summaryOrderRef) summaryOrderRef.textContent = ref;
}
initOrderRef();

// Toast Helper
function showToast(message: string, isError = false) {
  toastEl.textContent = message;
  toastEl.style.borderColor = isError ? 'var(--color-danger)' : 'var(--border-subtle)';
  toastEl.classList.remove('hidden');
  setTimeout(() => {
    toastEl.classList.add('hidden');
  }, 3500);
}

// Passkey Modal
function showAuthModal() {
  passkeyInput.value = currentPasskey;
  if (apiUrlInput) apiUrlInput.value = currentApiBase;
  authModal.classList.remove('hidden');
  passkeyInput.focus();
}

function hideAuthModal() {
  authModal.classList.add('hidden');
}

savePasskeyBtn.addEventListener('click', () => {
  const val = passkeyInput.value.trim();
  currentPasskey = val;
  localStorage.setItem('phonepe_passkey', val);

  if (apiUrlInput) {
    const urlVal = apiUrlInput.value.trim().replace(/\/+$/, '');
    currentApiBase = urlVal;
    localStorage.setItem('ppp_api_url', urlVal);
  }

  hideAuthModal();
  showToast('Settings saved! Reconnecting...');
  refreshAll();
});

authBtn.addEventListener('click', showAuthModal);

// Clipboard Helper
(window as any).copyToClipboard = (text: string, label: string) => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => {
    showToast(`Copied ${label}!`);
  }).catch(() => {
    showToast('Failed to copy', true);
  });
};

// Generic API Client
async function apiRequest<T = any>(path: string, options: RequestInit = {}): Promise<T | null> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as any)
  };

  if (currentPasskey) {
    headers['X-Access-Token'] = currentPasskey;
    headers['Authorization'] = `Bearer ${currentPasskey}`;
  }

  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const targetUrl = path.startsWith('http') ? path : `${currentApiBase}${cleanPath}`;

  try {
    const res = await fetch(targetUrl, { ...options, headers });
    if (res.status === 401) {
      showToast('Authentication required (Invalid Passkey)', true);
      showAuthModal();
      return null;
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Request failed (${res.status})`);
    }
    return await res.json();
  } catch (err: any) {
    console.error('API Error:', err);
    showToast(err.message || 'Network request failed', true);
    return null;
  }
}

// Formatters
function formatINR(val: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(val);
}

function formatTimeIST(ts: number): string {
  if (!ts) return '--';
  return new Date(ts).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatRelativeTime(ts: number): string {
  if (!ts) return '--';
  const diffSec = Math.floor((Date.now() - ts) / 1000);
  if (diffSec < 10) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  return `${diffHr}h ago`;
}

// -------------------------------------------------------------
// Load Stats
// -------------------------------------------------------------
async function loadStats() {
  const data = await apiRequest<{ success: boolean; stats: DashboardStats }>('/api/transactions/stats');
  if (!data || !data.stats) return;

  const { stats } = data;
  todayAmountEl.textContent = formatINR(stats.todayAmount || 0);
  todayCountEl.textContent = String(stats.todayCount || 0);
  totalAmountEl.textContent = formatINR(stats.totalAmount || 0);
  totalCountEl.textContent = String(stats.totalCount || 0);

  if (stats.lastSyncTimestamp) {
    lastSyncAgoEl.textContent = formatRelativeTime(stats.lastSyncTimestamp);
  } else {
    lastSyncAgoEl.textContent = 'None';
  }

  sessionIndicator.className = 'status-pill ' + (stats.isSessionActive ? 'status-active' : 'status-inactive');
  sessionStatusText.textContent = stats.isSessionActive ? 'Active' : 'Offline';
}

// -------------------------------------------------------------
// Load Transactions (Desktop Table + Mobile Cards)
// -------------------------------------------------------------
async function loadTransactions() {
  const query = new URLSearchParams({
    page: String(currentPage),
    limit: '25'
  });
  if (currentSearch) {
    query.set('search', currentSearch);
  }

  const data = await apiRequest<{
    success: boolean;
    total: number;
    totalAmount?: number;
    page: number;
    totalPages: number;
    transactions: Transaction[];
  }>(`/api/transactions/success?${query.toString()}`);

  if (!data) return;

  const { transactions, total, page, totalPages } = data;

  if (transactions.length === 0) {
    const emptyMsg = currentSearch ? 'No matching completed transactions.' : 'No successful transactions recorded yet in Cloudflare D1.';
    txTableBody.innerHTML = `<tr><td colspan="8" class="table-placeholder">${emptyMsg}</td></tr>`;
    txCardsList.innerHTML = `<div class="table-placeholder">${emptyMsg}</div>`;
    paginationInfo.textContent = 'Showing 0 of 0';
    pageIndicator.textContent = 'Page 1';
    prevPageBtn.disabled = true;
    nextPageBtn.disabled = true;
    return;
  }

  // 1. Render Desktop Table (Success Only)
  txTableBody.innerHTML = transactions.map((t) => {
    const isSettled = t.settlementStatus === 'SETTLED';
    const settlementBadge = `<span class="badge ${isSettled ? 'badge-settled' : 'badge-pending'}">${t.settlementStatus || 'PENDING'}</span>`;
    const appBadge = `<span class="badge badge-app">${t.paymentApp || 'UPI'}</span>`;
    const orderRef = t.merchantTransactionId ? `<span style="font-size:11px; color:#a5b4fc; font-weight:600; display:block;">Ref: ${t.merchantTransactionId}</span>` : '';
    const safeNote = (t.transactionNote || '').replace(/'/g, "\\'");

    return `
      <tr>
        <td>
          <div style="font-weight:600;">${formatTimeIST(t.transactionDate)}</div>
          <div style="font-size:11px; color:var(--text-dim);">${formatRelativeTime(t.transactionDate)}</div>
        </td>
        <td>
          <span class="amount-badge">₹${Number(t.amount).toFixed(2)}</span>
        </td>
        <td>
          <div style="display:flex; flex-direction:column; gap:2px;">
            <span style="font-weight:600;">${t.payerName || 'Anonymous Customer'}</span>
            <span style="font-family:var(--font-mono); font-size:11px; color:var(--text-muted);">${t.payerVpa || 'UPI Payer'}</span>
            ${t.transactionNote ? `<span style="font-size:11px; color:var(--text-dim); font-style:italic;">"${t.transactionNote}"</span>` : ''}
          </div>
        </td>
        <td>
          <div class="utr-pill" onclick="copyToClipboard('${t.utr || t.transactionId || ''}', 'UTR')" title="Tap to copy UTR">
            <span>${t.utr || t.transactionId || 'N/A'}</span>
          </div>
          ${orderRef}
        </td>
        <td>
          <div style="display:flex; flex-direction:column; font-size:12px;">
            <span style="font-weight:600;">${t.storeName || 'Store'}</span>
            <span style="font-size:11px; color:var(--text-dim);">${t.terminalName || 'Terminal'}</span>
          </div>
        </td>
        <td>${appBadge}</td>
        <td>
          <span class="badge badge-settled">COMPLETED</span>
          ${settlementBadge}
        </td>
        <td>
          <button type="button" class="btn btn-secondary btn-sm" style="padding:4px 8px; font-size:11px;" onclick="openOrderModal('${t.utr || t.unitId}', '${t.merchantTransactionId || ''}', '${safeNote}')" title="Edit Custom Order ID">
            ✏️ Ref
          </button>
        </td>
      </tr>
    `;
  }).join('');

  // 2. Render Mobile Cards (Success Only)
  txCardsList.innerHTML = transactions.map((t) => {
    const isSettled = t.settlementStatus === 'SETTLED';
    const settlementBadge = `<span class="badge ${isSettled ? 'badge-settled' : 'badge-pending'}">${t.settlementStatus || 'PENDING'}</span>`;
    const appBadge = `<span class="badge badge-app">${t.paymentApp || 'UPI'}</span>`;
    const safeNote = (t.transactionNote || '').replace(/'/g, "\\'");

    return `
      <div class="mobile-card">
        <div class="mobile-card-top">
          <span class="amount-badge">₹${Number(t.amount).toFixed(2)}</span>
          <div style="display:flex; gap:6px; align-items:center;">
            ${appBadge}
            <span class="badge badge-settled">COMPLETED</span>
            ${settlementBadge}
          </div>
        </div>

        <div class="mobile-card-customer">
          <span class="customer-name">${t.payerName || 'Anonymous Customer'}</span>
          <span class="customer-vpa">${t.payerVpa || 'UPI Payer'}</span>
          ${t.merchantTransactionId ? `<span style="font-size:11px; color:#a5b4fc; font-weight:600;">Order ID: ${t.merchantTransactionId}</span>` : ''}
          ${t.transactionNote ? `<span style="font-size:11px; color:var(--text-dim); font-style:italic;">Note: "${t.transactionNote}"</span>` : ''}
        </div>

        <div class="mobile-card-footer">
          <div style="display:flex; gap:6px; align-items:center;">
            <div class="utr-pill" onclick="copyToClipboard('${t.utr || t.transactionId || ''}', 'UTR')">
              <span>UTR: ${t.utr || t.transactionId || 'N/A'}</span>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" style="padding:2px 8px; font-size:11px;" onclick="openOrderModal('${t.utr || t.unitId}', '${t.merchantTransactionId || ''}', '${safeNote}')">
              ✏️ Ref
            </button>
          </div>
          <div style="text-align:right;">
            <div>${formatRelativeTime(t.transactionDate)}</div>
            <div style="font-size:10px; color:var(--text-dim);">${t.terminalName || 'Terminal'}</div>
          </div>
        </div>
      </div>
    `;
  }).join('');

  const startIdx = (page - 1) * 25 + 1;
  const endIdx = Math.min(total, page * 25);
  paginationInfo.textContent = `Showing ${startIdx}-${endIdx} of ${total} success payments`;
  pageIndicator.textContent = `Page ${page} of ${Math.max(1, totalPages)}`;
  prevPageBtn.disabled = page <= 1;
  nextPageBtn.disabled = page >= totalPages;
}

function refreshAll() {
  loadStats();
  loadTransactions();
  loadUpiList();
  loadApiKeys();
}

// Search debouncing
let searchDebounce: any = null;
searchInput.addEventListener('input', () => {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    currentSearch = searchInput.value.trim();
    currentPage = 1;
    loadTransactions();
  }, 300);
});

// Pagination
prevPageBtn.addEventListener('click', () => {
  if (currentPage > 1) {
    currentPage--;
    loadTransactions();
  }
});

nextPageBtn.addEventListener('click', () => {
  currentPage++;
  loadTransactions();
});

// Manual poll & reconcile
pollBtn.addEventListener('click', async () => {
  pollBtn.disabled = true;
  showToast('Polling PPP live stream...');
  const res = await apiRequest<{ success: boolean; newCount: number; error?: string }>('/api/poll/manual', {
    method: 'POST'
  });
  pollBtn.disabled = false;
  if (res?.success) {
    showToast(`Live poll done: ${res.newCount} new payment(s)!`);
    refreshAll();
  } else {
    showToast(`Poll failed: ${res?.error || 'Unknown'}`, true);
  }
});

reconcileBtn.addEventListener('click', async () => {
  reconcileBtn.disabled = true;
  showToast('Running reconciliation catchup...');
  const res = await apiRequest<{ success: boolean; processedCount: number; newCount: number; updatedCount: number; error?: string }>(
    '/api/reconcile/run',
    {
      method: 'POST',
      body: JSON.stringify({ lookbackHours: 24 })
    }
  );
  reconcileBtn.disabled = false;
  if (res?.success) {
    showToast(`Reconciled: ${res.processedCount} processed!`);
    refreshAll();
  } else {
    showToast(`Reconciliation failed: ${res?.error || 'Unknown'}`, true);
  }
});

refreshBtn.addEventListener('click', refreshAll);

// Auto-refresh
function setupAutoRefresh() {
  if (autoRefreshTimer) clearInterval(autoRefreshTimer);
  if (autoRefreshToggle.checked) {
    autoRefreshTimer = setInterval(() => {
      refreshAll();
    }, 10000);
  }
}
autoRefreshToggle.addEventListener('change', setupAutoRefresh);

// -------------------------------------------------------------
// Test Payment Console Logic (One At A Time & Direct UPI Deeplink)
// -------------------------------------------------------------
let currentCheckoutMode: 'pay' | 'qr' = 'pay';

// Prefill saved VPA if any
const savedVpa = localStorage.getItem('phonepe_test_vpa');
if (savedVpa && testVpaInput) {
  testVpaInput.value = savedVpa;
}

function updatePayAmountDisplay(amountNum: number) {
  if (payBtnLabel) {
    payBtnLabel.textContent = `Pay ₹${amountNum.toFixed(2)} via UPI`;
  }
}

function getUpiDetails(): { url: string; amt: number; ref: string } {
  const amt = parseFloat(testAmountInput.value) || 1.0;
  const vpa = testVpaInput.value.trim() || 'phonepe.business@ybl';
  const ref = testRefInput.value.trim() || `TEST-${Date.now()}`;
  const note = testNoteInput.value.trim() || 'Sentinel Test';

  if (vpa) {
    localStorage.setItem('phonepe_test_vpa', vpa);
  }

  const params = `pa=${encodeURIComponent(vpa)}&pn=PPP&am=${amt.toFixed(2)}&tr=${encodeURIComponent(ref)}&tn=${encodeURIComponent(note)}&cu=INR`;
  return {
    url: `upi://pay?${params}`,
    amt,
    ref
  };
}

function updateQrCode() {
  const { url, amt, ref } = getUpiDetails();
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(url)}`;
  if (testQrImage && testQrImage.src !== qrUrl) {
    testQrImage.src = qrUrl;
  }
  startRadarWatcher(ref, amt);
}

function startRadarWatcher(ref: string, amt: number) {
  if (radarText) {
    radarText.textContent = `Listening for incoming ₹${amt.toFixed(2)} payment...`;
    radarText.style.color = '#a5b4fc';
  }

  if (watcherTimer) clearInterval(watcherTimer);
  watcherTimer = setInterval(async () => {
    const check = await apiRequest<{ success: boolean; transactions: Transaction[] }>(
      `/api/transactions?search=${encodeURIComponent(ref)}&limit=1`
    );
    if (check?.transactions && check.transactions.length > 0) {
      clearInterval(watcherTimer);
      if (radarText) {
        radarText.textContent = `🎉 Payment of ₹${amt.toFixed(2)} CONFIRMED in D1!`;
        radarText.style.color = '#34d399';
      }
      showToast(`Payment received for ${ref}!`);
      refreshAll();
    }
  }, 3000);
}

// Mode Switcher (One at a time)
function setCheckoutMode(mode: 'pay' | 'qr') {
  currentCheckoutMode = mode;
  if (mode === 'pay') {
    modePayBtn.classList.add('active');
    modeQrBtn.classList.remove('active');
    payView.classList.remove('hidden');
    qrView.classList.add('hidden');
  } else {
    modeQrBtn.classList.add('active');
    modePayBtn.classList.remove('active');
    qrView.classList.remove('hidden');
    payView.classList.add('hidden');
    updateQrCode();
  }
}

modePayBtn.addEventListener('click', () => setCheckoutMode('pay'));
modeQrBtn.addEventListener('click', () => setCheckoutMode('qr'));

// Preset Amount Chips
presetChips.forEach((chip) => {
  chip.addEventListener('click', () => {
    presetChips.forEach((c) => c.classList.remove('active'));
    chip.classList.add('active');
    const amt = parseFloat(chip.getAttribute('data-amount') || '1');
    testAmountInput.value = amt.toFixed(2);
    updatePayAmountDisplay(amt);
    if (currentCheckoutMode === 'qr') {
      updateQrCode();
    }
  });
});

testAmountInput.addEventListener('input', () => {
  const val = parseFloat(testAmountInput.value) || 0;
  presetChips.forEach((chip) => {
    const chipAmt = parseFloat(chip.getAttribute('data-amount') || '0');
    if (chipAmt === val) {
      chip.classList.add('active');
    } else {
      chip.classList.remove('active');
    }
  });
  updatePayAmountDisplay(val);
  if (currentCheckoutMode === 'qr') {
    updateQrCode();
  }
});

// Parameter Inputs
testRefInput.addEventListener('input', () => {
  if (summaryOrderRef) summaryOrderRef.textContent = testRefInput.value || 'ORDER-...';
  if (currentCheckoutMode === 'qr') {
    updateQrCode();
  }
});

testVpaInput.addEventListener('change', () => {
  if (currentCheckoutMode === 'qr') {
    updateQrCode();
  }
});

testNoteInput.addEventListener('input', () => {
  if (currentCheckoutMode === 'qr') {
    updateQrCode();
  }
});

const copyRefBtn = document.getElementById('copyRefBtn') as HTMLButtonElement;
if (copyRefBtn) {
  copyRefBtn.addEventListener('click', () => {
    if (testRefInput?.value) {
      (window as any).copyToClipboard(testRefInput.value, 'Order ID');
    }
  });
}

// Deeplink Execution
function openDeeplink(url: string) {
  if (!url) return;
  showToast('Launching UPI app...');
  window.location.href = url;
}

payDirectBtn.addEventListener('click', () => {
  const { url, amt, ref } = getUpiDetails();
  startRadarWatcher(ref, amt);
  openDeeplink(url);
});

copyUpiLinkBtn.addEventListener('click', () => {
  const { url } = getUpiDetails();
  (window as any).copyToClipboard(url, 'UPI Deeplink');
});

// -------------------------------------------------------------
// Instant UTR Verifier Logic
// -------------------------------------------------------------
lookupForm.addEventListener('submit', async (e) => {
  e.preventDefault();
  const utr = lookupInput.value.trim();
  if (!utr) return;

  lookupSubmitBtn.disabled = true;
  lookupSubmitBtn.textContent = 'Verifying...';
  lookupResultBox.classList.add('hidden');

  const res = await apiRequest<{ success: boolean; source?: string; transaction?: any; error?: string; message?: string }>(
    '/api/transactions/lookup',
    {
      method: 'POST',
      body: JSON.stringify({ utr })
    }
  );

  lookupSubmitBtn.disabled = false;
  lookupSubmitBtn.textContent = 'Verify Now';

  if (res?.success && res.transaction) {
    const tx = res.transaction;
    lookupResultBox.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
        <span class="amount-badge">₹${Number(tx.amount).toFixed(2)}</span>
        <span class="badge badge-settled">${tx.paymentState || 'COMPLETED'}</span>
      </div>
      <div style="font-size:13px; line-height:1.6; color:var(--text-main);">
        <div>👤 <b>Payer:</b> ${tx.payerName || 'N/A'} (<code>${tx.payerVpa || 'UPI'}</code>)</div>
        <div>🏦 <b>UTR / RRN:</b> <code>${tx.utr || tx.transactionId}</code></div>
        <div>🏪 <b>Store:</b> ${tx.storeName || 'Main'} - ${tx.terminalName || 'Terminal'}</div>
        <div>🕒 <b>Timestamp:</b> ${formatTimeIST(tx.transactionDate)}</div>
        <div style="margin-top:8px; font-size:11px; color:var(--text-dim);">Source: <b>${res.source || 'Cloudflare D1'}</b></div>
      </div>
    `;
    lookupResultBox.classList.remove('hidden');
  } else {
    lookupResultBox.innerHTML = `
      <div style="color:var(--color-danger); font-size:13px;">
        ❌ <b>Not Found:</b> ${res?.message || 'No transaction record found with this UTR.'}
      </div>
    `;
    lookupResultBox.classList.remove('hidden');
  }
});

// -------------------------------------------------------------
// Cloudflare Browser Login Tab Logic
// -------------------------------------------------------------
const dashBrowserPhone = document.getElementById('dashBrowserPhone') as HTMLInputElement;
const dashBrowserSendOtpBtn = document.getElementById('dashBrowserSendOtpBtn') as HTMLButtonElement;
const dashBrowserStep2 = document.getElementById('dashBrowserStep2') as HTMLDivElement;
const dashBrowserOtp = document.getElementById('dashBrowserOtp') as HTMLInputElement;
const dashBrowserVerifyBtn = document.getElementById('dashBrowserVerifyBtn') as HTMLButtonElement;
const dashBrowserTestEngineBtn = document.getElementById('dashBrowserTestEngineBtn') as HTMLButtonElement;
const dashBrowserStatusBox = document.getElementById('dashBrowserStatusBox') as HTMLDivElement;
const dashBrowserScreenshotBox = document.getElementById('dashBrowserScreenshotBox') as HTMLDivElement;
const dashBrowserImg = document.getElementById('dashBrowserImg') as HTMLImageElement;
const dashScreenshotTime = document.getElementById('dashScreenshotTime') as HTMLSpanElement;

function setDashBrowserStatus(msg: string, type: 'success' | 'error' | 'info' = 'info') {
  if (!dashBrowserStatusBox) return;
  dashBrowserStatusBox.className = 'status-box ' + (type === 'error' ? 'status-inactive' : 'status-active');
  dashBrowserStatusBox.style.display = 'block';
  dashBrowserStatusBox.innerHTML = msg;
}

function showDashBrowserScreenshot(dataUrl: string) {
  if (!dataUrl || !dashBrowserImg || !dashBrowserScreenshotBox) return;
  dashBrowserImg.src = dataUrl;
  dashBrowserScreenshotBox.classList.remove('hidden');
  if (dashScreenshotTime) dashScreenshotTime.textContent = new Date().toLocaleTimeString();
}

if (dashBrowserTestEngineBtn) {
  dashBrowserTestEngineBtn.addEventListener('click', async () => {
    dashBrowserTestEngineBtn.disabled = true;
    showToast('Testing Chromium browser engine...');
    setDashBrowserStatus('⏳ Launching Cloudflare Puppeteer & navigating to PhonePe Business...', 'info');

    const res = await apiRequest<{ success: boolean; message: string; screenshot?: string }>('/api/browser/test');
    dashBrowserTestEngineBtn.disabled = false;

    if (res?.success) {
      setDashBrowserStatus(`✅ <b>Browser Ready:</b> ${res.message}`, 'success');
      if (res.screenshot) showDashBrowserScreenshot(res.screenshot);
      showToast('Browser engine test passed!');
    } else {
      setDashBrowserStatus(`❌ <b>Test Failed:</b> ${res?.message || 'Error running browser'}`, 'error');
      showToast('Browser test failed', true);
    }
  });
}

if (dashBrowserSendOtpBtn) {
  dashBrowserSendOtpBtn.addEventListener('click', async () => {
    const mobile = (dashBrowserPhone?.value || '').trim().replace(/\D/g, '');
    if (mobile.length !== 10) {
      showToast('Enter valid 10-digit mobile number', true);
      return;
    }

    dashBrowserSendOtpBtn.disabled = true;
    showToast('Requesting PhonePe OTP via Cloud Browser...');
    setDashBrowserStatus(`⏳ Cloudflare Browser is navigating to PhonePe and requesting OTP for +91 ${mobile}...`, 'info');

    const res = await apiRequest<{ success: boolean; message: string; screenshot?: string }>(
      '/api/browser/send-otp',
      {
        method: 'POST',
        body: JSON.stringify({ mobileNumber: mobile })
      }
    );

    dashBrowserSendOtpBtn.disabled = false;

    if (res?.success) {
      setDashBrowserStatus(`📲 <b>${res.message}</b><br>Enter the OTP received on your phone below.`, 'success');
      if (res.screenshot) showDashBrowserScreenshot(res.screenshot);
      if (dashBrowserStep2) {
        dashBrowserStep2.style.opacity = '1';
        dashBrowserStep2.style.pointerEvents = 'auto';
      }
      if (dashBrowserOtp) dashBrowserOtp.focus();
      showToast('OTP sent to your phone!');
    } else {
      setDashBrowserStatus(`❌ <b>Failed:</b> ${res?.message || 'Unable to trigger OTP'}`, 'error');
      showToast('Failed to trigger OTP', true);
    }
  });
}

if (dashBrowserVerifyBtn) {
  dashBrowserVerifyBtn.addEventListener('click', async () => {
    const mobile = (dashBrowserPhone?.value || '').trim().replace(/\D/g, '');
    const otp = (dashBrowserOtp?.value || '').trim().replace(/\D/g, '');

    if (!otp) {
      showToast('Please enter the OTP received', true);
      return;
    }

    dashBrowserVerifyBtn.disabled = true;
    showToast('Submitting OTP & capturing session...');
    setDashBrowserStatus(`⏳ Submitting OTP, capturing session cookies and updating D1 SQL...`, 'info');

    const res = await apiRequest<{ success: boolean; message: string; merchantId?: string; screenshot?: string }>(
      '/api/browser/verify-otp',
      {
        method: 'POST',
        body: JSON.stringify({ mobileNumber: mobile, otp })
      }
    );

    dashBrowserVerifyBtn.disabled = false;

    if (res?.success) {
      setDashBrowserStatus(`🎉 <b>Login Successful!</b><br>${res.message}<br>Merchant: <code>${res.merchantId}</code>`, 'success');
      if (res.screenshot) showDashBrowserScreenshot(res.screenshot);
      showToast('PhonePe Connected & Poller Active!');
      refreshAll();
    } else {
      setDashBrowserStatus(`❌ <b>Verification Failed:</b> ${res?.message || 'OTP verification failed'}`, 'error');
      if (res?.screenshot) showDashBrowserScreenshot(res.screenshot);
      showToast('Verification failed', true);
    }
  });
}

// -------------------------------------------------------------
// Inline Edit Order ID Modal Logic
// -------------------------------------------------------------
const editOrderModal = document.getElementById('editOrderModal') as HTMLDivElement;
const modalTargetUtr = document.getElementById('modalTargetUtr') as HTMLInputElement;
const modalOrderId = document.getElementById('modalOrderId') as HTMLInputElement;
const modalOrderNote = document.getElementById('modalOrderNote') as HTMLInputElement;
const closeOrderModalBtn = document.getElementById('closeOrderModalBtn') as HTMLButtonElement;
const saveOrderModalBtn = document.getElementById('saveOrderModalBtn') as HTMLButtonElement;

(window as any).openOrderModal = (target: string, currentOrder: string, note: string) => {
  if (modalTargetUtr) modalTargetUtr.value = target;
  if (modalOrderId) modalOrderId.value = currentOrder || '';
  if (modalOrderNote) modalOrderNote.value = note || '';
  if (editOrderModal) editOrderModal.classList.remove('hidden');
  if (modalOrderId) modalOrderId.focus();
};

function closeOrderModal() {
  if (editOrderModal) editOrderModal.classList.add('hidden');
}

if (closeOrderModalBtn) {
  closeOrderModalBtn.addEventListener('click', closeOrderModal);
}

if (saveOrderModalBtn) {
  saveOrderModalBtn.addEventListener('click', async () => {
    const target = (modalTargetUtr?.value || '').trim();
    const orderId = (modalOrderId?.value || '').trim();
    const note = (modalOrderNote?.value || '').trim();

    if (!orderId && !note) {
      showToast('Please enter an Order ID or Note', true);
      return;
    }

    saveOrderModalBtn.disabled = true;
    showToast('Saving Order ID in SQLite D1...');

    const res = await apiRequest<{ success: boolean; message: string; transaction?: any; error?: string }>(
      '/api/v1/transactions/update-ref',
      {
        method: 'POST',
        body: JSON.stringify({
          utr: target,
          unitId: target,
          merchantTransactionId: orderId,
          transactionNote: note
        })
      }
    );

    saveOrderModalBtn.disabled = false;

    if (res?.success) {
      showToast(`Order ID saved for ${target}!`);
      closeOrderModal();
      refreshAll();
    } else {
      showToast(`Failed: ${res?.error || 'Transaction not found'}`, true);
    }
  });
}

// -------------------------------------------------------------
// Secure UPI & Order Reference Management Logic
// -------------------------------------------------------------
const upiListContainer = document.getElementById('upiListContainer') as HTMLDivElement;
const refreshUpiBtn = document.getElementById('refreshUpiBtn') as HTMLButtonElement;
const registerUpiForm = document.getElementById('registerUpiForm') as HTMLFormElement;
const regUpiId = document.getElementById('regUpiId') as HTMLInputElement;
const regAccountHolder = document.getElementById('regAccountHolder') as HTMLInputElement;
const regBankName = document.getElementById('regBankName') as HTMLInputElement;
const regUpiSubmitBtn = document.getElementById('regUpiSubmitBtn') as HTMLButtonElement;

const updateRefForm = document.getElementById('updateRefForm') as HTMLFormElement;
const updateRefTarget = document.getElementById('updateRefTarget') as HTMLInputElement;
const updateRefOrderId = document.getElementById('updateRefOrderId') as HTMLInputElement;
const updateRefNote = document.getElementById('updateRefNote') as HTMLInputElement;
const updateRefSubmitBtn = document.getElementById('updateRefSubmitBtn') as HTMLButtonElement;

async function loadUpiList() {
  if (!upiListContainer) return;
  const res = await apiRequest<{ success: boolean; total: number; upis: any[] }>('/api/v1/upi/list');

  if (!res || !res.upis) {
    upiListContainer.innerHTML = '<div class="table-placeholder">Unable to load registered UPIs.</div>';
    return;
  }

  if (res.upis.length === 0) {
    upiListContainer.innerHTML = '<div class="table-placeholder">No UPI IDs registered yet. Register your first protected VPA above.</div>';
    return;
  }

  upiListContainer.innerHTML = res.upis.map((u) => {
    return `
      <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 12px; display: flex; justify-content: space-between; align-items: center;">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-family: var(--font-mono); font-weight: 700; color: #818cf8; font-size: 13px;">${u.upiId}</span>
            <span class="badge badge-settled" style="font-size: 9px; padding: 2px 6px;">🔒 LOCKED</span>
          </div>
          <span style="font-size: 12px; color: var(--text-main); font-weight: 600;">${u.accountHolderName}</span>
          ${u.bankName ? `<span style="font-size: 11px; color: var(--text-dim);">${u.bankName}</span>` : ''}
        </div>
        <button type="button" class="btn btn-secondary btn-sm" style="padding: 4px 8px; font-size: 11px;" onclick="copyToClipboard('${u.upiId}', 'UPI VPA')">
          Copy
        </button>
      </div>
    `;
  }).join('');
}

if (refreshUpiBtn) {
  refreshUpiBtn.addEventListener('click', () => {
    showToast('Refreshing registered UPI list...');
    loadUpiList();
  });
}

if (registerUpiForm) {
  registerUpiForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const upiId = (regUpiId?.value || '').trim().toLowerCase();
    const accountHolderName = (regAccountHolder?.value || '').trim();
    const bankName = (regBankName?.value || '').trim();

    if (!upiId || !upiId.includes('@')) {
      showToast('Please enter a valid UPI ID (e.g. storename@ybl)', true);
      return;
    }

    if (!accountHolderName) {
      showToast('Please enter Account Holder Name', true);
      return;
    }

    regUpiSubmitBtn.disabled = true;
    showToast('Registering and locking UPI VPA...');

    const res = await apiRequest<{ success: boolean; message: string; upi?: any; error?: string }>(
      '/api/v1/upi/register',
      {
        method: 'POST',
        body: JSON.stringify({ upiId, accountHolderName, bankName })
      }
    );

    regUpiSubmitBtn.disabled = false;

    if (res?.success) {
      showToast(`🎉 UPI ID '${upiId}' registered and locked successfully!`);
      registerUpiForm.reset();
      loadUpiList();
    } else {
      showToast(`Registration failed: ${res?.message || res?.error || 'UPI ID already locked'}`, true);
    }
  });
}

if (updateRefForm) {
  updateRefForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const target = (updateRefTarget?.value || '').trim();
    const orderId = (updateRefOrderId?.value || '').trim();
    const note = (updateRefNote?.value || '').trim();

    if (!target) {
      showToast('Please provide UTR or Unit ID', true);
      return;
    }

    if (!orderId && !note) {
      showToast('Please provide New Order ID or Note', true);
      return;
    }

    updateRefSubmitBtn.disabled = true;
    showToast('Updating transaction reference...');

    const res = await apiRequest<{ success: boolean; message: string; transaction?: any; error?: string }>(
      '/api/v1/transactions/update-ref',
      {
        method: 'POST',
        body: JSON.stringify({
          utr: target,
          unitId: target,
          merchantTransactionId: orderId,
          transactionNote: note
        })
      }
    );

    updateRefSubmitBtn.disabled = false;

    if (res?.success) {
      showToast(`Order ID '${orderId}' attached successfully!`);
      updateRefForm.reset();
      refreshAll();
    } else {
      showToast(`Update failed: ${res?.message || res?.error || 'Transaction not found'}`, true);
    }
  });
}

// -------------------------------------------------------------
// Developer Secret API Keys Management Logic
// -------------------------------------------------------------
const createApiKeyForm = document.getElementById('createApiKeyForm') as HTMLFormElement;
const newKeyNameInput = document.getElementById('newKeyNameInput') as HTMLInputElement;
const createKeyBtn = document.getElementById('createKeyBtn') as HTMLButtonElement;
const newKeyDisplayBox = document.getElementById('newKeyDisplayBox') as HTMLDivElement;
const newRawKeyInput = document.getElementById('newRawKeyInput') as HTMLInputElement;
const copyRawKeyBtn = document.getElementById('copyRawKeyBtn') as HTMLButtonElement;
const apiKeysListContainer = document.getElementById('apiKeysListContainer') as HTMLDivElement;

async function loadApiKeys() {
  if (!apiKeysListContainer) return;
  const res = await apiRequest<{ success: boolean; total: number; keys: ApiKey[] }>('/api/v1/keys/list');

  if (!res || !res.keys) {
    apiKeysListContainer.innerHTML = '<div class="table-placeholder">Unable to load active API keys.</div>';
    return;
  }

  if (res.keys.length === 0) {
    apiKeysListContainer.innerHTML = '<div class="table-placeholder">No secret API keys generated yet. Generate your first developer key above.</div>';
    return;
  }

  apiKeysListContainer.innerHTML = res.keys.map((k) => {
    const isRevoked = k.status === 'REVOKED';
    const statusBadge = isRevoked
      ? '<span class="badge badge-unsettled" style="font-size: 9px; padding: 2px 6px;">REVOKED</span>'
      : '<span class="badge badge-settled" style="font-size: 9px; padding: 2px 6px;">ACTIVE</span>';
    const lastUsedText = k.lastUsedAt ? formatRelativeTime(k.lastUsedAt) : 'Never used';
    const createdText = formatRelativeTime(k.createdAt);

    return `
      <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid var(--border-subtle); border-radius: 10px; padding: 12px; display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap;">
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-weight: 700; color: #34d399; font-size: 13px;">${k.name}</span>
            ${statusBadge}
          </div>
          <div style="font-family: var(--font-mono); font-size: 12px; color: var(--text-dim);">
            ${k.keyPrefix}••••••••••••
          </div>
          <div style="font-size: 11px; color: var(--text-dim);">
            Created ${createdText} • Last used: ${lastUsedText}
          </div>
        </div>
        <div>
          ${!isRevoked ? `
            <button type="button" class="btn btn-secondary btn-sm" style="padding: 4px 8px; font-size: 11px; color: #f87171;" onclick="revokeApiKey('${k.keyId}', '${k.name}')">
              Revoke
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }).join('');
}

(window as any).revokeApiKey = async (keyId: string, name: string) => {
  if (!confirm(`Are you sure you want to revoke API key "${name}"? This action cannot be undone.`)) {
    return;
  }
  showToast(`Revoking API key "${name}"...`);
  const res = await apiRequest<{ success: boolean; message?: string; error?: string }>('/api/v1/keys/revoke', {
    method: 'POST',
    body: JSON.stringify({ keyId })
  });

  if (res?.success) {
    showToast(`Key "${name}" revoked!`);
    loadApiKeys();
  } else {
    showToast(`Failed to revoke key: ${res?.error || 'Unknown error'}`, true);
  }
};

if (createApiKeyForm) {
  createApiKeyForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const name = (newKeyNameInput?.value || '').trim();
    if (!name) {
      showToast('Please enter a key name', true);
      return;
    }

    createKeyBtn.disabled = true;
    showToast('Generating new secret API key...');

    const res = await apiRequest<{ success: boolean; apiKey?: { rawKey: string; name: string }; error?: string }>(
      '/api/v1/keys/create',
      {
        method: 'POST',
        body: JSON.stringify({ name })
      }
    );

    createKeyBtn.disabled = false;

    if (res?.success && res.apiKey) {
      showToast(`Key "${res.apiKey.name}" created!`);
      newKeyNameInput.value = '';
      if (newKeyDisplayBox && newRawKeyInput) {
        newRawKeyInput.value = res.apiKey.rawKey;
        newKeyDisplayBox.classList.remove('hidden');
      }
      loadApiKeys();
    } else {
      showToast(`Failed to generate key: ${res?.error || 'Unknown error'}`, true);
    }
  });
}

if (copyRawKeyBtn && newRawKeyInput) {
  copyRawKeyBtn.addEventListener('click', () => {
    if (newRawKeyInput.value) {
      (window as any).copyToClipboard(newRawKeyInput.value, 'Secret API Key');
    }
  });
}

// Bootstrap
refreshAll();
setupAutoRefresh();
